import { Logic } from '~common/ui-logic/logic'
import type { AppContextType } from '~/app-context'
import type { DateRange } from 'react-day-picker'
import { ExportService } from '~/features/google-api/services/export-service'

export interface ExportColumnSelectionDependencies {
    exportType: 'bank-transactions' | 'invoices'
    onClose: () => void
}

export interface ExportColumnSelectionState {
    selectedColumns: Set<string>
    dateRange: DateRange | undefined
    exportState: 'idle' | 'exporting' | 'success' | 'error'
    exportError?: string
    exportedSpreadsheet?: {
        spreadsheetId: string
        spreadsheetUrl: string
    }
}

export class ExportColumnSelectionLogic extends Logic<
    ExportColumnSelectionDependencies & AppContextType,
    ExportColumnSelectionState
> {
    getInitialState = (): ExportColumnSelectionState => {
        const defaultDateRange: DateRange = {
            from: new Date(2025, 0, 1),
            to: new Date(),
        }

        const allColumns = this.getAllColumns()
        const selectedColumns = new Set(allColumns)

        return {
            selectedColumns,
            dateRange: undefined,
            exportState: 'idle',
        }
    }

    getAllColumns(): string[] {
        if (this.deps.exportType === 'bank-transactions') {
            return [
                'id',
                'accountIban',
                'vendorId',
                'vendorName',
                'counterpartyIban',
                'date',
                'amount',
                'currency',
                'referenceText',
                'note',
                'categoryId',
                'categoryLabel',
                'matchingInvoiceUrl',
                'status',
                'createdAt',
                'updatedAt',
            ]
        } else {
            return [
                'id',
                'date',
                'amount',
                'netAmount',
                'currency',
                'vatRate',
                'vatAmount',
                'invoiceId',
                'creditCard',
                'vendorName',
                'vendorAddress',
                'vendorCountry',
                'vendorIbans',
                'vendorEmailAddresses',
                'reason',
                'googleDriveFileId',
                'googleDriveFileLink',
                'sourceType',
                'createdAt',
                'updatedAt',
            ]
        }
    }

    toggleColumn(column: string, checked: boolean): void {
        const newSelected = new Set(this.state.selectedColumns)
        if (checked) {
            newSelected.add(column)
        } else {
            newSelected.delete(column)
        }
        this.setState({ selectedColumns: newSelected })
    }

    setDateRange(dateRange: DateRange | undefined): void {
        this.setState({ dateRange })
    }

    async exportToGoogleSheets(): Promise<void> {
        if (this.state.selectedColumns.size === 0) {
            this.setState({
                exportState: 'error',
                exportError: 'Please select at least one column to export',
            })
            return
        }

        this.setState({
            exportState: 'exporting',
            exportError: undefined,
        })

        try {
            const exportService = new ExportService({
                services: this.deps.services,
                storageModules: this.deps.storageModules,
            })

            const spreadsheet =
                this.deps.exportType === 'bank-transactions'
                    ? await exportService.exportTransactionsToGoogleSheets({
                          columns: Array.from(this.state.selectedColumns),
                          dateRange: this.state.dateRange ?? undefined,
                      })
                    : await exportService.exportInvoicesToGoogleSheets({
                          columns: Array.from(this.state.selectedColumns),
                          dateRange: this.state.dateRange ?? undefined,
                      })

            this.setState({
                exportState: 'success',
                exportedSpreadsheet: spreadsheet,
            })
        } catch (error) {
            console.error('Export failed:', error)
            this.setState({
                exportState: 'error',
                exportError:
                    error instanceof Error ? error.message : 'Export failed',
            })
        }
    }

    openExportedSpreadsheet(): void {
        if (this.state.exportedSpreadsheet?.spreadsheetUrl) {
            window.open(this.state.exportedSpreadsheet.spreadsheetUrl, '_blank')
        }
    }
}
